import { allocate, type Money } from "../money/money";

/**
 * Shared-bill statutory discount proration (FR-030, T173).
 *
 * The published methodology (BIR RR 7-2010's implementation of RA 9994 /
 * RA 10754) splits the eligible amount evenly PER HEAD, then applies the
 * VAT-exemption-and-20%-off treatment to the covered heads' pooled share as
 * ONE calculation — not once per covered person. Computing it per person and
 * summing introduces exactly the kind of one-centavo rounding drift the
 * published worked examples show (three seniors sharing ₱3,000 sum to
 * ₱2,142.87 computed individually, ₱2,142.86 computed as one pool) — this
 * fixture's cases in tests/fixtures/pricing/statutory.json are hand-verified
 * against the pooled figure, which is also the one `allocate`'s
 * largest-remainder method guarantees sums exactly with the uncovered share.
 */

export class ProrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProrationError";
  }
}

export interface Proration {
  /** The pooled share belonging to statutory-covered customers. */
  coveredShare: Money;
  /** The remainder — taxed and priced normally, no exemption or discount. */
  uncoveredShare: Money;
}

export function prorateForCoverage(
  eligibleAmount: Money,
  coveredCustomers: number,
  totalCustomers: number,
): Proration {
  if (!Number.isInteger(totalCustomers) || totalCustomers <= 0) {
    throw new ProrationError(`totalCustomers must be a positive whole number, got ${totalCustomers}`);
  }
  if (!Number.isInteger(coveredCustomers) || coveredCustomers < 0) {
    throw new ProrationError(
      `coveredCustomers must be a non-negative whole number, got ${coveredCustomers}`,
    );
  }
  if (coveredCustomers > totalCustomers) {
    throw new ProrationError(
      `coveredCustomers (${coveredCustomers}) cannot exceed totalCustomers (${totalCustomers})`,
    );
  }

  const uncoveredCustomers = totalCustomers - coveredCustomers;
  const [coveredShare, uncoveredShare] = allocate(eligibleAmount, [
    coveredCustomers,
    uncoveredCustomers,
  ]);

  return { coveredShare: coveredShare!, uncoveredShare: uncoveredShare! };
}
